import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CalendarDaysIcon, MapPinIcon } from '@heroicons/react/24/outline';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

interface Event {
  id: string;
  title: string;
  description: string;
  date: string;
  endDate?: string;
  location: string;
  price: number;
  category: string;
  image: string;
  availableSpots: number;
  totalSpots: number;
}

const EventsPage: React.FC = () => {
  const [events, setEvents] = useState<Event[]>([]);
  const [filteredEvents, setFilteredEvents] = useState<Event[]>([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  // Mock events data - Replace with API call
  const mockEvents: Event[] = useMemo(() => [
    {
      id: '1',
      title: 'Advanced Collision Repair Workshop',
      description: 'Comprehensive training on the latest collision repair techniques and technologies. Learn from industry experts.',
      date: '2026-05-15',
      location: 'Chicago, IL',
      price: 299,
      category: 'Workshop',
      image: 'event-1.jpg',
      availableSpots: 15,
      totalSpots: 30,
    },
    {
      id: '2',
      title: 'Paint Technology Seminar',
      description: 'Discover cutting-edge paint technologies and application techniques for superior finishes.',
      date: '2026-05-22',
      location: 'Dallas, TX',
      price: 199,
      category: 'Seminar',
      image: '1.jpg',
      availableSpots: 8,
      totalSpots: 25,
    },
    {
      id: '3',
      title: 'Industry Networking Summit',
      description: 'Connect with industry leaders, vendors, and peers in this exclusive networking event.',
      date: '2026-06-05',
      endDate: '2026-06-06',
      location: 'Miami, FL',
      price: 399,
      category: 'Conference',
      image: 'landing.jpg',
      availableSpots: 45,
      totalSpots: 100,
    },
    {
      id: '4',
      title: 'Digital Estimating Masterclass',
      description: 'Master digital estimating tools and techniques to improve accuracy and efficiency.',
      date: '2026-06-12',
      location: 'Phoenix, AZ',
      price: 249,
      category: 'Workshop',
      image: 'hero.jpg',
      availableSpots: 20,
      totalSpots: 35,
    },
    {
      id: '5',
      title: 'Safety & Compliance Training',
      description: 'Essential safety protocols and compliance requirements for collision repair facilities.',
      date: '2026-06-18',
      location: 'Atlanta, GA',
      price: 179,
      category: 'Training',
      image: 'event-1.jpg',
      availableSpots: 30,
      totalSpots: 50,
    },
    {
      id: '6',
      title: 'Business Growth Strategies',
      description: 'Learn proven strategies to grow your collision repair business and increase profitability.',
      date: '2026-06-25',
      location: 'Las Vegas, NV',
      price: 349,
      category: 'Conference',
      image: '1.jpg',
      availableSpots: 12,
      totalSpots: 40,
    },
  ], []);

  const categories = ['all', 'Workshop', 'Seminar', 'Conference', 'Training'];

  useEffect(() => {
    // Simulate API loading
    setTimeout(() => {
      setEvents(mockEvents);
      setFilteredEvents(mockEvents);
      setLoading(false);
    }, 1000);
  }, [mockEvents]);

  useEffect(() => {
    let filtered = events;

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(event => event.category === selectedCategory);
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(event =>
        event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.location.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredEvents(filtered);
  }, [events, selectedCategory, searchTerm]);

  const formatDate = (dateString: string, endDateString?: string) => {
    const date = new Date(dateString);
    const formatted = date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });

    if (endDateString) {
      const endDate = new Date(endDateString);
      const endFormatted = endDate.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
      return `${formatted} - ${endFormatted}`;
    }

    return formatted;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navigation />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Professional Events
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl">
              Advance your career with industry-leading workshops, seminars, and networking events 
              designed for collision repair professionals.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          {/* Search */}
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search events..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Category Filter */}
          <div>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              {categories.map(category => (
                <option key={category} value={category}>
                  {category === 'all' ? 'All Categories' : category}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Events Grid */}
        {filteredEvents.length === 0 ? (
          <div className="text-center py-12">
            <h3 className="text-lg font-medium text-gray-900 mb-2">No events found</h3>
            <p className="text-gray-600">Try adjusting your search or filter criteria.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredEvents.map((event, index) => (
              <motion.div
                key={event.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300 group"
              >
                <div className="relative">
                  <img
                    src={`/${event.image}`}
                    alt={event.title}
                    className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-primary-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                      {event.category}
                    </span>
                  </div>
                  <div className="absolute top-4 right-4">
                    <span className="bg-white text-primary-600 px-3 py-1 rounded-full text-sm font-bold">
                      ${event.price}
                    </span>
                  </div>
                </div>

                <div className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-primary-600 transition-colors">
                    {event.title}
                  </h3>
                  
                  <p className="text-gray-600 mb-4 line-clamp-2">
                    {event.description}
                  </p>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center text-sm text-gray-500">
                      <CalendarDaysIcon className="w-4 h-4 mr-2" />
                      {formatDate(event.date, event.endDate)}
                    </div>
                    <div className="flex items-center text-sm text-gray-500">
                      <MapPinIcon className="w-4 h-4 mr-2" />
                      {event.location}
                    </div>
                  </div>

                  <div className="flex items-center justify-between mb-4">
                    <div className="text-sm">
                      <span className="text-gray-500">Available: </span>
                      <span className={`font-medium ${
                        event.availableSpots < 10 ? 'text-red-600' : 'text-green-600'
                      }`}>
                        {event.availableSpots}/{event.totalSpots} spots
                      </span>
                    </div>
                    {event.availableSpots < 10 && (
                      <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded-full">
                        Limited
                      </span>
                    )}
                  </div>

                  <div className="flex gap-3">
                    <Link
                      to={`/events/${event.id}`}
                      className="flex-1 text-center bg-primary-600 text-white px-4 py-2 rounded-md font-medium hover:bg-primary-700 transition-colors"
                    >
                      View Details
                    </Link>
                    <Link
                      to={`/checkout/${event.id}`}
                      className="bg-gray-100 text-gray-700 px-4 py-2 rounded-md font-medium hover:bg-gray-200 transition-colors"
                    >
                      Book Now
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default EventsPage;